import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useJobs, jobIsActive, useLiveConfig } from '../data/firestore';
import { appKit, dollars, platformFree, platformStart } from '../data/plans';
import { LOGIN_URL } from '../data/env';
import { track } from '../utils/track';
import { OrgStrip } from './PlatformNav';
import WorkNav, { FOUNDERS } from './WorkNav';
import NavMark from './NavMarks';

const APP_NAME = process.env.REACT_APP_APP_NAME || 'starter';

function PersonLink({ to, mark, children, onClick }) {
  return (
    <Link to={to} className="cs-nav-person" onClick={onClick}>
      <NavMark name={mark} />
      {children}
    </Link>
  );
}

// One chooser, three rungs: the sandbox and Platform are the quiet
// segments, App Kit is the filled end ([[homepage-conversion-first]] —
// paid above the fold). Prices come from plans.js so they track the tiers.
function ProductCarts({ onClick }) {
  const go = (tier) => () => { track('cta_click', { where: 'nav', plan: tier.id, label: tier.cta }); onClick(); };
  return (
    <div className="cs-nav-carts" role="group" aria-label="Start">
      <Link to={platformFree.href} className="cs-nav-cart" onClick={go(platformFree)}>Try free</Link>
      <Link to={platformStart.href} className="cs-nav-cart" onClick={go(platformStart)}>Platform · {dollars(platformStart)}</Link>
      <Link to={appKit.href} className="cs-nav-cart is-main" onClick={go(appKit)}>App Kit · {dollars(appKit)}</Link>
    </div>
  );
}

// Grouped dropdown: click-to-toggle on desktop (closes on outside click,
// Escape, and navigation); the mobile breakpoint flattens it into a labeled
// section inside the burger menu (CSS disables the trigger there).
// NavGroup, NavItem and NavKicker are shared with the signed-in WorkNav.
export function NavItem({ to, hint, onClick, end, mark, feature, children }) {
  const inner = (
    <>
      {feature ? (
        <span
          className="cs-nav-still"
          style={{ backgroundImage: `url(${feature})` }}
          aria-hidden="true"
        />
      ) : null}
      <span className="cs-nav-item-row">
        {mark ? <NavMark name={mark} /> : null}
        <span className="cs-nav-item-copy">
          <span>{children}</span>
          {hint ? <small>{hint}</small> : null}
        </span>
      </span>
    </>
  );
  const className = `cs-nav-item${feature ? ' has-still' : ''}`;
  if (to.includes('#')) {
    return <Link to={to} className={className} onClick={onClick}>{inner}</Link>;
  }
  return (
    <NavLink to={to} end={end} className={className} onClick={onClick}>
      {inner}
    </NavLink>
  );
}

export function NavKicker({ children }) {
  return <span className="cs-nav-kicker">{children}</span>;
}

function NavCol({ kicker, children }) {
  return (
    <div className="cs-nav-col">
      {kicker ? <NavKicker>{kicker}</NavKicker> : null}
      {children}
    </div>
  );
}

export function NavGroup({ label, active, badge, menu, mega, children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={`cs-nav-group ${open ? 'open' : ''}`} ref={ref}>
      <button
        type="button"
        className={`cs-nav-group-btn ${active ? 'active' : ''}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        {badge > 0 && <span className="cs-nav-jobs-badge">{badge}</span>}
        <span className="cs-nav-caret" aria-hidden="true">▾</span>
      </button>
      <div className={`cs-nav-drop ${menu ? 'cs-nav-menu' : ''} ${mega ? 'cs-nav-mega' : ''}`}>{children}</div>
    </div>
  );
}

// Public host. Three bars by state (decision signed-in-work-bar):
// visitors get the marketing bar below; a member inside /orgs/:id gets the
// OrgStrip (decision ux-single-nav-in-org); a member anywhere else gets the
// WorkNav — the marketing menus never show to a signed-in account.
export default function Navbar() {
  const { user } = useAuth();
  // Anonymous sessions (public chat agent) get the visitor navbar.
  const member = !!user && !user.isAnonymous;
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const jobs = useJobs(100, member);
  const activeJobs = (jobs || []).filter(jobIsActive).length;
  const { doc: liveCfg } = useLiveConfig();
  const handbookReader = member && (FOUNDERS.includes(user.email)
    || (liveCfg?.docs?.engineeringReaders || []).includes(user.email));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Collapse the mobile menu whenever we navigate to a new route.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const close = () => setOpen(false);
  const under = (...prefixes) => prefixes.some((p) => location.pathname.startsWith(p));
  const onKit = under('/app-kit', '/dev-console');
  const onPlatform = (location.pathname === '/platform' || under('/platform')) && !onKit;

  // Inside an organization both hosts show the same utility strip
  // (decision ux-single-nav-in-org): the OrgShell sidebar owns movement.
  const orgMatch = location.pathname.match(/^\/orgs\/([^/]+)(\/|$)/);
  if (member && orgMatch) {
    return (
      <OrgStrip
        orgId={orgMatch[1]}
        user={user}
        founder={FOUNDERS.includes(user.email)}
        handbookReader={handbookReader}
        activeJobs={activeJobs}
      />
    );
  }

  if (member) return <WorkNav />;

  return (
    <nav className={`cs-nav ${scrolled || open ? 'scrolled' : ''} ${open ? 'open' : ''}`}>
      <Link to="/" className="cs-brand" onClick={close}>
        <img src="/logo.png" alt={APP_NAME} className="cs-brand-logo" />
      </Link>
      {/* Under the burger breakpoint the account block is folded into the
          menu; sign-in stays one tap away beside ☰. */}
      <a href={LOGIN_URL} className="cs-btn cs-btn-outline cs-btn-sm cs-nav-mini" onClick={close}>Login</a>
      <button
        type="button"
        className="cs-nav-burger"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
      >
        {open ? '✕' : '☰'}
      </button>
      <div className="cs-nav-links">
        <NavGroup label="Platform" active={onPlatform} menu mega>
          <div className="cs-nav-cols">
            <NavCol>
              <NavItem to="/platform" end mark="overview" feature="/art/modules/nqube.png" hint="Workflows, agents, and the runtime" onClick={close}>Overview</NavItem>
              <NavKicker>The loop</NavKicker>
              <NavItem to="/#proof" mark="loop" hint="Invoice intake, gated" onClick={close}>How it works</NavItem>
            </NavCol>
            <NavCol kicker="Design and runtime">
              <NavItem to="/platform/nqube" mark="nqube" hint="Design the workflow" onClick={close}>nQube</NavItem>
              <NavItem to="/platform/training-center" mark="training" hint="Pre-flight before the GPU" onClick={close}>Training Center</NavItem>
              <NavItem to="/platform/agentos" mark="agentos" hint="The runtime that commits" onClick={close}>agentOS</NavItem>
            </NavCol>
            <NavCol kicker="Agents">
              <NavItem to="/platform/agent-io" mark="agent-io" hint="Define the agent" onClick={close}>Agent IO</NavItem>
              <NavItem to="/platform/nventr-agent" mark="nventr-agent" hint="One conversation on a page" onClick={close}>nventr Agent</NavItem>
              <NavItem to="/platform/knowledge" mark="knowledge" hint="What they may know" onClick={close}>Knowledge</NavItem>
              <NavItem to="/platform/human-review" mark="review" hint="The person at the gate" onClick={close}>Human Review</NavItem>
            </NavCol>
            <NavCol kicker="Capabilities">
              <NavItem to="/platform/integrations" mark="integrations" hint="Systems a write may touch" onClick={close}>Integrations</NavItem>
              <NavItem to="/platform/listen" mark="listen" hint="Data in" onClick={close}>Listen</NavItem>
              <NavItem to="/platform/athena" mark="athena" hint="What the runtime did" onClick={close}>Athena</NavItem>
              <NavItem to="/platform/devops" mark="devops" hint="Deploys, health and logs" onClick={close}>DevOps</NavItem>
              <NavItem to="/security" mark="security" hint="SSO, audit, policy history, the Vault" onClick={close}>Security</NavItem>
              <NavItem to="/works-with" mark="integrations" hint="Clouds, identity, sources, systems, models" onClick={close}>Works with</NavItem>
            </NavCol>
          </div>
        </NavGroup>
        <NavGroup label="App Kit" active={onKit} menu>
          <NavItem to="/dev-console" mark="dev-console" hint="The engineering team inside your app · included" onClick={close}>Dev Console</NavItem>
          <NavItem to="/app-kit" mark="kit" hint="The instance you rent" onClick={close}>Overview</NavItem>
          <NavKicker>On the instance</NavKicker>
          <NavItem to="/app-kit#foundation" mark="foundation" hint="What the fee pays for" onClick={close}>The foundation</NavItem>
          <NavItem to="/app-kit#assurance" mark="assurance" hint="Health, checks, and a baseline" onClick={close}>Assurance</NavItem>
        </NavGroup>
        <NavGroup label="More" active={under('/store', '/partners', '/physical')} menu>
          <NavItem to="/store" mark="hardware" onClick={close}>Hardware</NavItem>
          <NavItem to="/partners" mark="partners" onClick={close}>Partners</NavItem>
          <NavItem to="/physical" mark="gridos" onClick={close}>GridOS</NavItem>
        </NavGroup>
        <NavLink to="/pricing" onClick={close}>
          Pricing
        </NavLink>
      </div>
      <div className="cs-nav-spacer" />
      <div className="cs-nav-user">
        <PersonLink to="/contact?subject=Sales" mark="sales" onClick={close}>Sales</PersonLink>
        <PersonLink to="/support" mark="support" onClick={close}>Support</PersonLink>
        <a href={LOGIN_URL} className="cs-btn cs-btn-outline cs-btn-sm" onClick={close}>
          Login
        </a>
        <ProductCarts onClick={close} />
      </div>
    </nav>
  );
}
